import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import CategoryIcon from "@/components/CategoryIcon";
import { useApp } from "@/context/AppContext";
import API from "@/lib/api";
import { Search, ArrowUpRight, ArrowRight, Play, LogIn, Home, ClipboardList, MessageCircle, User, Plus, MapPin, ChevronRight, Bell, ShieldCheck } from "lucide-react";

const FONTS = "https://fonts.googleapis.com/css2?family=Archivo+Black&family=Space+Grotesk:wght@400;500;700&family=Archivo:wght@400;500;600;800;900&family=DM+Serif+Display:ital@0;1&family=JetBrains+Mono:wght@400;600&family=Manrope:wght@400;500;600;700;800&family=Figtree:wght@400;500;600;700&family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800&family=Outfit:wght@400;500;600;700;800&family=Schibsted+Grotesk:wght@400;500;600;700;800;900&family=Montserrat:wght@500;600;700;800&display=swap";
const C = { navy: "#0B0D2B", blue: "#2563EB", yellow: "#FFD23F", pink: "#FF4F86", paper: "#FDFCF7" };
const ROT = [C.blue, C.yellow, C.pink, C.navy, C.yellow, C.blue];
const SHAPES = ["50%", "0", "999px 999px 0 0", "0 50% 50% 0", "50% 0 50% 0", "0"];

function useFonts() {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONTS; document.head.appendChild(l);
    return () => { document.head.removeChild(l); };
  }, []);
}

function useHomeData() {
  const { categories, catName, macros } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState("need");
  const [q, setQ] = useState("");
  const [counts, setCounts] = useState({});
  useEffect(() => {
    API.get("/public/counts").then((r) => setCounts(r.data.counts || {})).catch(() => {});
  }, []);
  const items = mode === "need"
    ? macros.map((m) => ({ key: m.id, label: m.label, icon: m.icon, tint: m.tint, go: `/need/${m.id}` }))
    : categories.map((c) => ({ key: c.slug, label: catName(c), icon: c.icon, tint: c.tint, n: counts[c.slug] || 0, go: `/category/${c.slug}` }));
  const search = (e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}`); };
  return { mode, setMode, q, setQ, items, search, navigate };
}

/* ---------- 1. BAUHAUS CLASSICO ---------- */
function Bauhaus() {
  const d = useHomeData();
  const disp = { fontFamily: "'Archivo Black', sans-serif" };
  return (
    <div data-testid="demo-bauhaus" className="min-h-screen" style={{ background: C.paper, color: C.navy, fontFamily: "'Space Grotesk', sans-serif" }}>
      <header className="border-b-[3px] border-[#0B0D2B] flex items-stretch h-16">
        <div className="flex items-center gap-2 px-4 sm:px-8 border-r-[3px] border-[#0B0D2B]">
          <span className="w-5 h-5 rounded-full bg-[#FF4F86]" /><span className="w-5 h-5 bg-[#2563EB]" />
          <span className="text-2xl ml-1" style={disp}>FYDATE</span>
        </div>
        <div className="flex-1 hidden sm:flex items-center px-6 text-sm font-medium uppercase tracking-[0.2em]">La bacheca dei bisogni</div>
        <button data-testid="bauhaus-login" onClick={() => d.navigate("/login")} className="ml-auto px-6 bg-[#FFD23F] border-l-[3px] border-[#0B0D2B] font-bold uppercase text-sm hover:bg-[#FF4F86] transition-colors">Accedi</button>
      </header>

      <section className="grid md:grid-cols-12 border-b-[3px] border-[#0B0D2B]">
        <div className="md:col-span-7 p-6 sm:p-12 md:border-r-[3px] border-[#0B0D2B] demo-rise">
          <p className="uppercase tracking-[0.25em] text-xs font-bold mb-6">N°01 — Italia</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[0.92]" style={disp}>
            Chiedi.<br /><span className="text-[#2563EB]">Trova.</span><br /><span className="bg-[#FFD23F] px-2">Aiuta.</span>
          </h1>
          <p className="mt-8 max-w-md text-base sm:text-lg">Chi cerca un servizio incontra chi può offrirlo, nella tua zona. Gratis, senza intermediari.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button data-testid="bauhaus-video" className="h-12 px-6 bg-[#FF4F86] border-[3px] border-[#0B0D2B] shadow-[4px_4px_0_#0B0D2B] font-bold inline-flex items-center gap-2 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_#0B0D2B] transition-[transform,box-shadow]"><Play size={16} className="fill-current" /> Guarda il video</button>
            <button data-testid="bauhaus-how" className="h-12 px-6 bg-white border-[3px] border-[#0B0D2B] shadow-[4px_4px_0_#0B0D2B] font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_#0B0D2B] transition-[transform,box-shadow]">Come funziona</button>
          </div>
        </div>
        <div className="md:col-span-5 relative min-h-[260px] bg-[#2563EB] overflow-hidden">
          <div className="absolute w-56 h-56 rounded-full bg-[#FFD23F] -top-10 -right-10 demo-spin-slow" />
          <div className="absolute w-40 h-40 bg-[#FF4F86] bottom-8 left-8 rotate-12" />
          <div className="absolute left-1/2 top-1/2 w-0 h-0 border-l-[70px] border-r-[70px] border-b-[120px] border-l-transparent border-r-transparent border-b-[#0B0D2B]" />
          <div className="absolute bottom-0 right-0 w-1/2 h-6 bg-[#FDFCF7]" />
        </div>
      </section>

      <section className="grid sm:grid-cols-[auto_1fr] border-b-[3px] border-[#0B0D2B]">
        <div data-testid="bauhaus-toggle" className="flex">
          {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
            <button key={k} data-testid={`bauhaus-mode-${k}`} onClick={() => d.setMode(k)}
              className={`flex-1 sm:flex-none h-16 px-8 font-bold uppercase text-sm border-r-[3px] border-[#0B0D2B] transition-colors ${d.mode === k ? "bg-[#0B0D2B] text-[#FFD23F]" : "hover:bg-[#FFD23F]"}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={d.search} className="flex items-center border-t-[3px] sm:border-t-0 border-[#0B0D2B]">
          <Search className="ml-5 shrink-0" size={20} />
          <input data-testid="bauhaus-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività — es. gommista" className="flex-1 h-16 px-4 bg-transparent text-lg font-medium placeholder:text-[#0B0D2B]/50 focus:outline-none" />
        </form>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-3">
        {d.items.map((it, i) => {
          const bg = ROT[i % 6]; const dark = bg === C.navy || bg === C.blue;
          return (
            <button key={it.key} data-testid={`bauhaus-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className={`group relative text-left min-h-[180px] sm:min-h-[220px] p-5 sm:p-7 border-b-[3px] border-r-[3px] border-[#0B0D2B] overflow-hidden ${i % 3 === 1 ? "bg-white" : ""}`}>
              <span className="absolute -right-6 -bottom-6 w-20 h-20 sm:w-36 sm:h-36 transition-transform duration-500 group-hover:scale-125 group-hover:rotate-45" style={{ background: bg, borderRadius: SHAPES[i % 6] }} />
              <span className={`absolute z-10 right-4 bottom-4 ${dark ? "text-white" : "text-[#0B0D2B]"}`}><CategoryIcon name={it.icon} size={28} /></span>
              <span className="text-xs font-bold tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-xl sm:text-2xl leading-tight max-w-[80%] relative" style={disp}>{it.label}</h3>
              {it.n !== undefined && <p className="mt-2 text-sm font-bold relative">{it.n} richieste</p>}
            </button>
          );
        })}
      </section>

      <footer className="bg-[#0B0D2B] text-[#FDFCF7] p-6 sm:p-12 flex flex-col sm:flex-row sm:items-end gap-6">
        <h2 className="text-3xl sm:text-4xl leading-none" style={disp}>Hai un'attività?</h2>
        <button data-testid="bauhaus-business" onClick={() => d.navigate("/per-attivita")} className="sm:ml-auto h-12 px-6 bg-[#FFD23F] text-[#0B0D2B] font-bold inline-flex items-center gap-2 hover:bg-[#FF4F86] transition-colors">Scopri FYDATE per le attività <ArrowRight size={18} /></button>
      </footer>
    </div>
  );
}

/* ---------- 2. MINIMAL SVIZZERO ---------- */
function Swiss() {
  const d = useHomeData();
  return (
    <div data-testid="demo-swiss" className="min-h-screen bg-white text-[#0B0D2B]" style={{ fontFamily: "'Archivo', sans-serif" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <header className="flex items-baseline justify-between py-6 border-b border-[#0B0D2B]">
          <span className="text-xl font-black tracking-tight">FYDATE<span className="text-[#2563EB]">.</span></span>
          <nav className="flex items-baseline gap-6 text-sm">
            <button data-testid="swiss-business-nav" onClick={() => d.navigate("/per-attivita")} className="hidden sm:inline hover:text-[#2563EB] transition-colors">Per le attività</button>
            <button data-testid="swiss-login" onClick={() => d.navigate("/login")} className="font-semibold hover:text-[#2563EB] transition-colors inline-flex items-center gap-1">Accedi <LogIn size={14} /></button>
          </nav>
        </header>

        <section className="grid md:grid-cols-12 gap-6 pt-16 sm:pt-24 pb-16 demo-rise">
          <p className="md:col-span-3 text-xs uppercase tracking-[0.18em] text-[#0B0D2B]/60 pt-3">La bacheca<br />dei bisogni<br /><span className="text-[#2563EB]">— Italia</span></p>
          <div className="md:col-span-9">
            <h1 className="text-5xl sm:text-6xl lg:text-[96px] font-black leading-[0.9] tracking-[-0.04em]">
              Ti serve qualcosa.<br />Qualcuno vicino<br /><span className="text-[#2563EB]">può farlo.</span>
            </h1>
            <div className="mt-10 grid sm:grid-cols-2 gap-6 max-w-2xl">
              <p className="text-base text-[#0B0D2B]/70">Pubblica il tuo bisogno, ricevi disponibilità da persone e attività della tua zona. Gratis e senza intermediari.</p>
              <div className="flex gap-5 text-sm font-semibold items-start">
                <button data-testid="swiss-video" className="inline-flex items-center gap-1.5 border-b-2 border-[#2563EB] pb-1 hover:text-[#2563EB] transition-colors">Video <ArrowUpRight size={15} /></button>
                <button data-testid="swiss-how" className="inline-flex items-center gap-1.5 border-b-2 border-[#0B0D2B] pb-1 hover:text-[#2563EB] hover:border-[#2563EB] transition-colors">Come funziona <ArrowUpRight size={15} /></button>
              </div>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-12 gap-6 border-t border-[#0B0D2B] pt-6 pb-10">
          <div data-testid="swiss-toggle" className="md:col-span-3 flex md:flex-col gap-4">
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`swiss-mode-${k}`} onClick={() => d.setMode(k)} className={`text-left text-lg font-bold transition-colors ${d.mode === k ? "text-[#2563EB]" : "text-[#0B0D2B]/30 hover:text-[#0B0D2B]"}`}>
                {d.mode === k ? "→ " : ""}{l}
              </button>
            ))}
          </div>
          <form onSubmit={d.search} className="md:col-span-9 flex items-center border-b-2 border-[#0B0D2B] focus-within:border-[#2563EB] transition-colors">
            <input data-testid="swiss-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività" className="flex-1 h-14 text-2xl sm:text-3xl font-bold tracking-tight bg-transparent placeholder:text-[#0B0D2B]/20 focus:outline-none" />
            <button type="submit" data-testid="swiss-search-submit"><Search size={24} /></button>
          </form>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-[#0B0D2B]">
          {d.items.map((it, i) => (
            <button key={it.key} data-testid={`swiss-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group text-left py-6 sm:px-4 border-b border-[#0B0D2B]/15 flex items-start gap-4 hover:bg-[#2563EB] hover:text-white transition-colors">
              <span className="text-xs font-semibold tabular-nums pt-1.5 text-[#0B0D2B]/40 group-hover:text-white/70 w-6">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1">
                <h3 className="text-xl font-bold tracking-tight leading-tight">{it.label}</h3>
                {it.n !== undefined && <p className="text-sm mt-1 opacity-60">{it.n} richieste in zona</p>}
              </div>
              <ArrowUpRight size={20} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
            </button>
          ))}
        </section>

        <footer className="grid md:grid-cols-12 gap-6 py-20 text-sm">
          <p className="md:col-span-3 text-[#0B0D2B]/50">© FYDATE</p>
          <button data-testid="swiss-business" onClick={() => d.navigate("/per-attivita")} className="md:col-span-9 text-left text-3xl sm:text-4xl font-black tracking-tight hover:text-[#2563EB] transition-colors">Hai un'attività? Fatti trovare →</button>
        </footer>
      </div>
    </div>
  );
}

/* ---------- 3. EDITORIALE A GRIGLIA ---------- */
function Editorial() {
  const d = useHomeData();
  const serif = { fontFamily: "'DM Serif Display', serif" };
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const today = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div data-testid="demo-editorial" className="min-h-screen" style={{ background: C.paper, color: C.navy, fontFamily: "'Archivo', sans-serif" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-10">
        <div className="flex justify-between py-2 text-[11px] uppercase border-b border-[#0B0D2B]" style={mono}>
          <span>{today}</span><span className="hidden sm:inline">Edizione Italia</span>
          <button data-testid="editorial-login" onClick={() => d.navigate("/login")} className="hover:text-[#FF4F86]">Accedi ↗</button>
        </div>
        <header className="py-6 sm:py-8 border-b-[6px] border-double border-[#0B0D2B] text-center">
          <h1 className="text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight" style={serif}>FYDATE</h1>
          <p className="mt-2 text-xs uppercase tracking-[0.3em]" style={mono}>La bacheca dei bisogni</p>
        </header>

        <section className="grid md:grid-cols-12 border-b-2 border-[#0B0D2B]">
          <div className="md:col-span-8 py-8 md:pr-8 md:border-r border-[#0B0D2B] demo-rise">
            <p className="text-[11px] uppercase text-[#FF4F86] font-semibold mb-3" style={mono}>In primo piano</p>
            <h2 className="text-4xl sm:text-5xl leading-[1.02]" style={serif}>Il quartiere torna a darsi una mano. <em className="text-[#2563EB]">Senza intermediari.</em></h2>
            <p className="mt-5 text-base max-w-xl first-letter:text-5xl first-letter:float-left first-letter:mr-2 first-letter:leading-none">
              Chi cerca un servizio pubblica il suo bisogno; chi può offrirlo, nella stessa zona, risponde. Privati, attività e professionisti sulla stessa pagina.
            </p>
          </div>
          <aside className="md:col-span-4 py-8 md:pl-8 border-t md:border-t-0 border-[#0B0D2B] flex flex-col gap-4">
            <button data-testid="editorial-video" className="group text-left">
              <div className="aspect-video bg-[#0B0D2B] flex items-center justify-center relative overflow-hidden">
                <span className="absolute w-24 h-24 rounded-full bg-[#FF4F86] -left-6 -top-6" />
                <span className="w-14 h-14 rounded-full bg-[#FFD23F] flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={20} className="fill-[#0B0D2B]" /></span>
              </div>
              <p className="mt-2 text-sm" style={serif}>Guarda il video di lancio</p>
            </button>
            <button data-testid="editorial-how" className="text-left text-[11px] uppercase border-t border-[#0B0D2B] pt-3 hover:text-[#2563EB]" style={mono}>→ Come funziona</button>
          </aside>
        </section>

        <section className="flex flex-col sm:flex-row sm:items-center gap-4 py-5 border-b border-[#0B0D2B]">
          <div data-testid="editorial-toggle" className="flex text-[12px] uppercase" style={mono}>
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`editorial-mode-${k}`} onClick={() => d.setMode(k)}
                className={`px-4 h-10 border border-[#0B0D2B] -ml-px first:ml-0 transition-colors ${d.mode === k ? "bg-[#0B0D2B] text-[#FDFCF7]" : "hover:bg-[#FFD23F]"}`}>{l}</button>
            ))}
          </div>
          <form onSubmit={d.search} className="flex-1 flex items-center border border-[#0B0D2B] h-10 bg-white">
            <Search size={16} className="mx-3" />
            <input data-testid="editorial-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="cerca un'attività…" className="flex-1 bg-transparent text-sm focus:outline-none" style={mono} />
          </form>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {d.items.map((it, i) => (
            <button key={it.key} data-testid={`editorial-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group text-left py-7 sm:px-6 border-b border-[#0B0D2B] sm:[&:nth-child(odd)]:border-r lg:[&:nth-child(odd)]:border-r-0 lg:[&:not(:nth-child(3n))]:border-r flex gap-5 hover:bg-white transition-colors">
              <span className="text-6xl leading-none transition-colors group-hover:text-[#FF4F86]" style={{ ...serif, color: i % 2 ? C.blue : C.navy }}>{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 border-l border-[#0B0D2B]/20 pl-5">
                <h3 className="text-2xl leading-tight" style={serif}>{it.label}</h3>
                <p className="mt-2 text-[11px] uppercase text-[#0B0D2B]/60" style={mono}>
                  {it.n !== undefined ? `${it.n} richieste in zona` : "Leggi la sezione →"}
                </p>
              </div>
            </button>
          ))}
        </section>

        <footer className="my-12 bg-[#FFD23F] p-6 sm:p-10 grid md:grid-cols-12 gap-4 items-end">
          <h2 className="md:col-span-8 text-3xl sm:text-4xl leading-tight" style={serif}>Hai un'attività? <em>Il tuo quartiere ti sta cercando.</em></h2>
          <button data-testid="editorial-business" onClick={() => d.navigate("/per-attivita")} className="md:col-span-4 h-12 bg-[#0B0D2B] text-[#FDFCF7] text-[12px] uppercase hover:bg-[#FF4F86] transition-colors" style={mono}>Scopri FYDATE per le attività →</button>
        </footer>
      </div>
    </div>
  );
}

/* ---------- 4. SVIZZERO MORBIDO ---------- */
const DOTS = [C.blue, C.yellow, C.pink];
function SoftToggle({ d, id }) {
  return (
    <div data-testid={`${id}-toggle`} className="inline-flex p-1 rounded-full bg-[#F1F2F6]">
      {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
        <button key={k} data-testid={`${id}-mode-${k}`} onClick={() => d.setMode(k)}
          className={`h-10 px-5 rounded-full text-sm font-semibold transition-[background-color,color,box-shadow] ${d.mode === k ? "bg-white text-[#0B0D2B] shadow-[0_1px_3px_rgba(11,13,43,0.12)]" : "text-[#6A6F86] hover:text-[#0B0D2B]"}`}>{l}</button>
      ))}
    </div>
  );
}

function SoftSearch({ d, id }) {
  return (
    <form onSubmit={d.search} className="flex items-center h-12 rounded-xl bg-white border border-[#E6E7EE] focus-within:border-[#2563EB] focus-within:ring-4 focus-within:ring-[#2563EB]/10 transition-[border-color,box-shadow]">
      <Search size={18} className="ml-4 text-[#8A8FA6]" />
      <input data-testid={`${id}-search`} value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività (es. gommista)" className="flex-1 h-full px-3 bg-transparent text-[15px] placeholder:text-[#8A8FA6] focus:outline-none" />
    </form>
  );
}

function SoftSwiss() {
  const d = useHomeData();
  return (
    <div data-testid="demo-soft" className="min-h-screen bg-white text-[#0B0D2B]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="max-w-4xl mx-auto px-5">
        <header className="flex items-center justify-between h-16">
          <span className="flex items-center gap-2"><img src="/fydate-icon.png" alt="FYDATE" className="w-8 h-8 rounded-lg" /><span className="text-lg font-extrabold tracking-tight">FYDATE</span></span>
          <button data-testid="soft-login" onClick={() => d.navigate("/login")} className="h-9 px-4 rounded-full text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors">Accedi</button>
        </header>

        <section className="pt-10 sm:pt-14 pb-10 demo-rise">
          <div className="flex items-center gap-1.5 mb-4">
            {DOTS.map((c) => <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />)}
            <span className="ml-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#6A6F86]">La bacheca dei bisogni</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-[1.1] tracking-[-0.02em] max-w-xl">
            Ti serve qualcosa? <span className="text-[#2563EB]">Qualcuno vicino può aiutarti.</span>
          </h1>
          <p className="mt-4 text-[15px] text-[#4A4F68] max-w-lg">Racconta di cosa hai bisogno e ricevi disponibilità da persone e attività della tua zona. Gratis e senza intermediari.</p>
          <div className="mt-6 flex gap-5 text-sm font-semibold">
            <button data-testid="soft-video" className="inline-flex items-center gap-2 hover:text-[#2563EB] transition-colors"><span className="w-8 h-8 rounded-full bg-[#FFF3C4] flex items-center justify-center"><Play size={13} className="fill-[#0B0D2B]" /></span> Guarda il video</button>
            <button data-testid="soft-how" className="inline-flex items-center gap-1 text-[#6A6F86] hover:text-[#2563EB] transition-colors">Come funziona <ArrowRight size={15} /></button>
          </div>
        </section>

        <section className="flex flex-col sm:flex-row gap-3 sm:items-center pb-6">
          <SoftToggle d={d} id="soft" />
          <div className="flex-1"><SoftSearch d={d} id="soft" /></div>
        </section>

        <section className="grid sm:grid-cols-2 gap-x-8 border-t border-[#ECEDF2]">
          {d.items.map((it, i) => (
            <button key={it.key} data-testid={`soft-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group flex items-center gap-4 py-4 border-b border-[#ECEDF2] text-left">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-105" style={{ background: it.tint?.iconBg, color: it.tint?.text }}><CategoryIcon name={it.icon} size={20} /></span>
              <span className="flex-1">
                <span className="block text-[15px] font-bold">{it.label}</span>
                {it.n !== undefined && <span className="block text-xs text-[#6A6F86]">{it.n > 0 ? `${it.n} richieste in zona` : "Nessuna richiesta"}</span>}
              </span>
              <span className="text-xs font-semibold text-[#B4B7C6] tabular-nums group-hover:hidden">{String(i + 1).padStart(2, "0")}</span>
              <ArrowRight size={18} className="hidden group-hover:block text-[#2563EB]" />
            </button>
          ))}
        </section>

        <footer className="my-14 rounded-2xl bg-[#F6F7FB] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-extrabold">Hai un'attività?</h2>
            <p className="text-sm text-[#4A4F68]">Fatti trovare da chi ha bisogno vicino a te.</p>
          </div>
          <button data-testid="soft-business" onClick={() => d.navigate("/per-attivita")} className="sm:ml-auto h-11 px-5 rounded-full bg-[#FFD23F] text-[#0B0D2B] text-sm font-bold hover:bg-[#F5C518] transition-colors inline-flex items-center gap-2">Scopri FYDATE per le attività <ArrowRight size={16} /></button>
        </footer>
      </div>
    </div>
  );
}

/* ---------- 5. ATTUALE CURATO ---------- */
function Refined() {
  const d = useHomeData();
  return (
    <div data-testid="demo-refined" className="min-h-screen bg-[#FAFAF7] text-[#0B0D2B]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      <header className="border-b border-[#ECECE6] bg-[#FAFAF7]/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center">
          <img src="/fydate-icon.png" alt="FYDATE" className="w-8 h-8 rounded-lg" />
          <span className="ml-2 text-lg font-bold tracking-tight">FYDATE</span>
          <button data-testid="refined-login" onClick={() => d.navigate("/login")} className="ml-auto h-9 px-4 rounded-full text-sm font-semibold border border-[#DADBE3] bg-white hover:border-[#2563EB] hover:text-[#2563EB] transition-colors">Accedi</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-6">
        <section data-testid="refined-intro" className="relative overflow-hidden rounded-2xl bg-white border border-[#ECECE6] p-6 sm:p-7 mb-6 demo-rise">
          <span className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#FFD23F]/25" />
          <span className="absolute right-10 top-12 w-3 h-3 rounded-full bg-[#FF4F86]" />
          <p className="text-xs font-semibold text-[#2563EB] mb-2">Benvenuto su FYDATE</p>
          <h1 className="text-2xl sm:text-3xl font-bold leading-tight max-w-md">La bacheca dei bisogni della tua zona</h1>
          <p className="mt-2 text-[15px] text-[#4A4F68] max-w-lg">Chi cerca un servizio incontra chi può offrirlo. Gratis e senza intermediari.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button data-testid="refined-video" className="h-10 px-4 rounded-full bg-[#0B0D2B] text-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#2563EB] transition-colors"><Play size={13} className="fill-white" /> Guarda il video</button>
            <button data-testid="refined-how" className="h-10 px-4 rounded-full bg-[#F1F2F6] text-sm font-semibold hover:bg-[#E6E8F0] transition-colors">Come funziona</button>
          </div>
        </section>

        <div className="flex justify-center mb-4"><SoftToggle d={d} id="refined" /></div>
        <div className="mb-6"><SoftSearch d={d} id="refined" /></div>

        <p className="text-sm font-semibold text-[#6A6F86] mb-3">{d.mode === "need" ? "Scegli un'area" : "Richieste vicino a te"}</p>
        <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {d.items.map((it) => (
            <button key={it.key} data-testid={`refined-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group text-left bg-white rounded-2xl border border-[#ECECE6] p-4 min-h-[128px] flex flex-col hover:-translate-y-0.5 hover:border-[#D5D7E0] hover:shadow-[0_6px_20px_-8px_rgba(11,13,43,0.15)] transition-[transform,box-shadow,border-color]">
              <span className="flex items-start justify-between">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: it.tint?.iconBg, color: it.tint?.text }}><CategoryIcon name={it.icon} size={20} /></span>
                {it.n !== undefined && <span className={`text-xs font-bold px-2 h-6 rounded-full flex items-center ${it.n > 0 ? "bg-[#2563EB]/10 text-[#2563EB]" : "bg-[#F1F2F6] text-[#8A8FA6]"}`}>{it.n}</span>}
              </span>
              <span className="mt-auto pt-4 flex items-end justify-between gap-2">
                <span className="text-[15px] font-semibold leading-tight">{it.label}</span>
                <ArrowRight size={16} className="shrink-0 text-[#B4B7C6] group-hover:text-[#2563EB] group-hover:translate-x-0.5 transition-[color,transform]" />
              </span>
            </button>
          ))}
        </section>

        <footer className="mt-12 mb-6 text-center">
          <button data-testid="refined-business" onClick={() => d.navigate("/per-attivita")} className="h-10 px-5 rounded-full bg-[#FFE4EE] text-[#C42766] text-sm font-semibold hover:bg-[#FFD3E3] transition-colors">Hai un'attività? Scopri FYDATE per le attività</button>
          <p className="mt-4 text-xs text-[#8A8FA6]">FYDATE — La bacheca dei bisogni</p>
        </footer>
      </main>
    </div>
  );
}

/* ---------- 6. VIVA ---------- */
const POP = [
  { bg: "#FFD23F", fg: "#0B0D2B" }, { bg: "#2563EB", fg: "#FFFFFF" }, { bg: "#FF4F86", fg: "#FFFFFF" },
  { bg: "#0B0D2B", fg: "#FFD23F" }, { bg: "#DCE8FF", fg: "#1D4ED8" }, { bg: "#FFE1EB", fg: "#C42766" },
];
const TILT = ["-rotate-2", "rotate-1", "-rotate-1", "rotate-2", "rotate-1", "-rotate-2"];

function Ticker({ items }) {
  const live = items.filter((i) => i.n > 0);
  const list = live.length ? live : items;
  const row = list.map((i) => (
    <span key={i.key} className="inline-flex items-center gap-2 mx-5">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF4F86] animate-pulse" />
      {i.n !== undefined ? `${i.n} richieste · ${i.label}` : `Nuove richieste · ${i.label}`}
    </span>
  ));
  return (
    <div data-testid="viva-ticker" className="overflow-hidden whitespace-nowrap bg-[#0B0D2B] text-white text-[13px] font-semibold py-2.5">
      <div className="inline-block demo-marquee">{row}{row}</div>
    </div>
  );
}

function Viva() {
  const d = useHomeData();
  const disp = { fontFamily: "'Bricolage Grotesque', sans-serif" };
  return (
    <div data-testid="demo-viva" className="min-h-screen bg-[#FDFCF7] text-[#0B0D2B]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="max-w-5xl mx-auto px-5 h-16 flex items-center">
        <img src="/fydate-icon.png" alt="FYDATE" className="w-9 h-9 rounded-xl -rotate-6 hover:rotate-6 transition-transform" />
        <span className="ml-2 text-xl font-extrabold tracking-tight" style={disp}>fydate</span>
        <button data-testid="viva-login" onClick={() => d.navigate("/login")} className="ml-auto h-10 px-5 rounded-full bg-[#0B0D2B] text-white text-sm font-semibold hover:bg-[#2563EB] hover:-translate-y-0.5 transition-[background-color,transform]">Accedi</button>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-6 pb-10 grid md:grid-cols-[1.3fr_1fr] gap-8 items-center">
        <div className="demo-rise">
          <span className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white border border-[#ECECE6] text-xs font-semibold">
            <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" /> Attivo nella tua zona
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.02] tracking-[-0.03em]" style={disp}>
            Hai bisogno?{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Chiedilo.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#FFD23F] -rotate-1 rounded" />
            </span>
            <br />Puoi aiutare? <span className="text-[#FF4F86]">Fallo.</span>
          </h1>
          <p className="mt-4 text-base text-[#4A4F68] max-w-md">La bacheca dei bisogni del quartiere. Chi cerca trova chi offre, in pochi tap. Gratis.</p>
          <div className="mt-6 flex flex-wrap gap-2">
            <button data-testid="viva-video" className="h-11 px-5 rounded-full bg-[#FF4F86] text-white text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-6px_#FF4F86] transition-[transform,box-shadow]"><Play size={14} className="fill-white" /> Guarda il video</button>
            <button data-testid="viva-how" className="h-11 px-5 rounded-full bg-white border border-[#E3E3DC] text-sm font-bold hover:border-[#0B0D2B] transition-colors">Come funziona</button>
          </div>
        </div>
        <div className="relative h-56 sm:h-64 hidden sm:block">
          <div className="absolute left-6 top-4 w-48 rounded-2xl bg-white border border-[#ECECE6] p-4 shadow-[0_12px_30px_-12px_rgba(11,13,43,0.25)] -rotate-3 demo-float">
            <p className="text-[11px] font-semibold text-[#6A6F86]">Richiesta · 2 km</p>
            <p className="mt-1 text-sm font-bold">Cerco idraulico per perdita in cucina</p>
            <span className="mt-3 inline-flex h-6 px-2 rounded-full bg-[#FFD23F] text-[11px] font-bold items-center">3 disponibili</span>
          </div>
          <div className="absolute right-2 bottom-2 w-48 rounded-2xl bg-[#2563EB] text-white p-4 shadow-[0_12px_30px_-12px_rgba(37,99,235,0.6)] rotate-3 demo-float" style={{ animationDelay: "1.2s" }}>
            <p className="text-[11px] font-semibold text-white/70">Disponibilità</p>
            <p className="mt-1 text-sm font-bold">Posso passare domani mattina!</p>
            <span className="mt-3 inline-flex h-6 px-2 rounded-full bg-white/20 text-[11px] font-bold items-center">Risposta in 5 min</span>
          </div>
          <span className="absolute right-16 top-0 w-10 h-10 rounded-full bg-[#FF4F86]" />
        </div>
      </section>

      <Ticker items={d.items} />

      <section className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6">
          <div data-testid="viva-toggle" className="inline-flex p-1 rounded-full bg-[#0B0D2B]">
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`viva-mode-${k}`} onClick={() => d.setMode(k)}
                className={`h-10 px-5 rounded-full text-sm font-bold transition-colors ${d.mode === k ? "bg-[#FFD23F] text-[#0B0D2B]" : "text-white/70 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <form onSubmit={d.search} className="flex-1 flex items-center h-12 rounded-full bg-white border-2 border-[#0B0D2B] focus-within:shadow-[3px_3px_0_#2563EB] transition-shadow">
            <Search size={18} className="ml-4" />
            <input data-testid="viva-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività… es. gommista" className="flex-1 h-full px-3 bg-transparent text-[15px] focus:outline-none" />
            <button type="submit" data-testid="viva-search-submit" className="mr-1 h-9 w-9 rounded-full bg-[#2563EB] text-white flex items-center justify-center hover:rotate-12 transition-transform"><ArrowRight size={16} /></button>
          </form>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {d.items.map((it, i) => {
            const p = POP[i % 6];
            return (
              <button key={it.key} data-testid={`viva-tile-${it.key}`} onClick={() => d.navigate(it.go)}
                className="group relative text-left rounded-3xl p-4 sm:p-5 min-h-[130px] sm:min-h-[150px] flex flex-col hover:-translate-y-1 hover:-rotate-1 active:scale-95 transition-transform duration-200 demo-pop"
                style={{ background: p.bg, color: p.fg, animationDelay: `${i * 50}ms` }}>
                <span className="flex items-start justify-between">
                  <span className="w-11 h-11 rounded-2xl bg-white/25 flex items-center justify-center group-hover:rotate-12 transition-transform"><CategoryIcon name={it.icon} size={22} /></span>
                  {it.n !== undefined && it.n > 0 && <span className={`h-7 px-2.5 rounded-full bg-white text-[#0B0D2B] text-xs font-extrabold flex items-center ${TILT[i % 6]}`}>{it.n} {it.n === 1 ? "nuova" : "nuove"}</span>}
                </span>
                <span className="mt-auto pt-4 flex items-end justify-between gap-2">
                  <span className="text-base sm:text-lg font-bold leading-tight" style={disp}>{it.label}</span>
                  <ArrowUpRight size={20} className="shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[opacity,transform]" />
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-5 pb-12">
        <div className="rounded-3xl bg-[#2563EB] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden">
          <span className="absolute -right-6 -bottom-10 w-32 h-32 rounded-full bg-[#FFD23F]" />
          <div className="relative">
            <h2 className="text-2xl font-extrabold" style={disp}>Hai un'attività?</h2>
            <p className="text-sm text-white/80">Ti cercano già. Fatti trovare.</p>
          </div>
          <button data-testid="viva-business" onClick={() => d.navigate("/per-attivita")} className="relative sm:ml-auto sm:mr-20 h-11 px-5 rounded-full bg-white text-[#0B0D2B] text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">Scopri come <ArrowRight size={16} /></button>
        </div>
      </footer>
    </div>
  );
}

/* ---------- 7. QUIETA ---------- */
const Q = { ink: "#1C1F3A", paper: "#F5F3EE", line: "#DCD8CE", blue: "#3F5E96", sand: "#E9DFC4", rose: "#E9CFD5", mist: "#D9E1EC", sage: "#DCE3D6" };
const QTILE = [Q.sand, Q.mist, Q.rose, Q.sage, Q.mist, Q.sand];

function Quieta() {
  const d = useHomeData();
  return (
    <div data-testid="demo-quieta" className="min-h-screen" style={{ background: Q.paper, color: Q.ink, fontFamily: "'Schibsted Grotesk', sans-serif" }}>
      <header className="max-w-5xl mx-auto px-5 h-16 flex items-center border-b" style={{ borderColor: Q.line }}>
        <img src="/fydate-icon.png" alt="FYDATE" className="w-8 h-8" />
        <span className="ml-2 text-lg font-black tracking-tight">FYDATE</span>
        <button data-testid="quieta-login" onClick={() => d.navigate("/login")} className="ml-auto h-10 px-5 text-sm font-bold text-white transition-colors hover:opacity-90" style={{ background: Q.ink }}>Accedi</button>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-10 pb-10 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
        <div className="demo-rise">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em]" style={{ color: Q.blue }}>
            <span className="w-2 h-2 bg-[#7FA37A]" /> Attivo nella tua zona
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black leading-[1.02] tracking-[-0.025em]">
            Hai bisogno? <span className="px-1" style={{ background: Q.sand }}>Chiedilo.</span><br />
            Puoi aiutare? <span style={{ color: Q.blue }}>Fallo.</span>
          </h1>
          <p className="mt-5 text-base max-w-md text-[#4F5268]">La bacheca dei bisogni del quartiere. Chi cerca trova chi offre, con calma e senza intermediari. Gratis.</p>
          <div className="mt-7 flex flex-wrap gap-2">
            <button data-testid="quieta-video" className="h-11 px-5 text-sm font-bold text-white inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform" style={{ background: Q.blue }}><Play size={14} className="fill-white" /> Guarda il video</button>
            <button data-testid="quieta-how" className="h-11 px-5 text-sm font-bold border-2 hover:bg-white transition-colors" style={{ borderColor: Q.ink }}>Come funziona</button>
          </div>
        </div>
        <div className="relative h-60 hidden sm:block">
          <div className="absolute left-4 top-2 w-52 bg-white border p-4 shadow-[6px_6px_0_#DCD8CE] demo-float" style={{ borderColor: Q.line }}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#8A8C9C]">Richiesta · 2 km</p>
            <p className="mt-1.5 text-sm font-bold">Cerco idraulico per perdita in cucina</p>
            <span className="mt-3 inline-flex h-6 px-2 text-[11px] font-bold items-center" style={{ background: Q.sand }}>3 disponibili</span>
          </div>
          <div className="absolute right-2 bottom-2 w-52 p-4 text-white shadow-[6px_6px_0_#C9CFDB] demo-float" style={{ background: Q.ink, animationDelay: "1.5s" }}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-white/60">Disponibilità</p>
            <p className="mt-1.5 text-sm font-bold">Posso passare domani mattina.</p>
            <span className="mt-3 inline-flex h-6 px-2 text-[11px] font-bold items-center bg-white/15">Risposta in 5 min</span>
          </div>
        </div>
      </section>

      <div data-testid="quieta-ticker" className="overflow-hidden whitespace-nowrap border-y text-[13px] font-semibold py-2.5" style={{ borderColor: Q.line, background: "#EEEBE3" }}>
        <div className="inline-block demo-marquee" style={{ animationDuration: "45s" }}>
          {[0, 1].map((k) => d.items.map((i) => (
            <span key={`${k}-${i.key}`} className="inline-flex items-center gap-2 mx-5">
              <span className="w-1.5 h-1.5" style={{ background: Q.blue }} />
              {i.n !== undefined ? `${i.n} richieste · ${i.label}` : `Nuove richieste · ${i.label}`}
            </span>
          )))}
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-stretch mb-6">
          <div data-testid="quieta-toggle" className="inline-flex border-2" style={{ borderColor: Q.ink }}>
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`quieta-mode-${k}`} onClick={() => d.setMode(k)}
                className={`flex-1 h-11 px-5 text-sm font-bold whitespace-nowrap transition-colors ${d.mode === k ? "text-white" : "hover:bg-white"}`} style={d.mode === k ? { background: Q.ink } : {}}>{l}</button>
            ))}
          </div>
          <form onSubmit={d.search} className="sm:flex-1 shrink-0 flex items-center h-12 bg-white border-2 focus-within:shadow-[4px_4px_0_#D9E1EC] transition-shadow" style={{ borderColor: Q.ink }}>
            <Search size={18} className="ml-4" />
            <input data-testid="quieta-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività… es. gommista" className="flex-1 h-full px-3 bg-transparent text-[15px] font-medium focus:outline-none" />
            <button type="submit" data-testid="quieta-search-submit" className="h-full w-12 text-white flex items-center justify-center" style={{ background: Q.ink }}><ArrowRight size={18} /></button>
          </form>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {d.items.map((it, i) => (
            <button key={it.key} data-testid={`quieta-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group relative text-left p-4 sm:p-5 min-h-[130px] sm:min-h-[150px] flex flex-col hover:-translate-y-1 hover:shadow-[5px_5px_0_#1C1F3A] active:translate-y-0 active:shadow-none transition-[transform,box-shadow] duration-200 demo-rise"
              style={{ background: QTILE[i % 6], animationDelay: `${i * 45}ms` }}>
              <span className="flex items-start justify-between">
                <span className="w-11 h-11 bg-white/70 flex items-center justify-center"><CategoryIcon name={it.icon} size={22} /></span>
                {it.n !== undefined && it.n > 0 && <span className="h-7 px-2.5 bg-white text-xs font-extrabold flex items-center">{it.n} {it.n === 1 ? "nuova" : "nuove"}</span>}
              </span>
              <span className="mt-auto pt-4 flex items-end justify-between gap-2">
                <span className="text-base sm:text-lg font-extrabold leading-tight">{it.label}</span>
                <ArrowRight size={18} className="shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-[opacity,transform]" />
              </span>
            </button>
          ))}
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-5 pb-12">
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4 text-white" style={{ background: Q.ink }}>
          <div>
            <h2 className="text-2xl font-black">Hai un'attività?</h2>
            <p className="text-sm text-white/70">Ti cercano già. Fatti trovare.</p>
          </div>
          <button data-testid="quieta-business" onClick={() => d.navigate("/per-attivita")} className="sm:ml-auto h-11 px-5 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform" style={{ background: Q.sand, color: Q.ink }}>Scopri come <ArrowRight size={16} /></button>
        </div>
      </footer>
    </div>
  );
}

/* ---------- 7b. QUIETA con temi colore ---------- */
const THEMES = {
  cartablu: { name: "Carta blu", bg: "#F5F2EA", strip: "#ECE7DB", ink: "#2E2C29", muted: "#615D56", line: "#DDD6C7", accent: "#4A6A9C", onAccent: "#FFFFFF", tiles: ["#EBE2CA", "#DCE4EF", "#EDD9DC", "#DFE6D8"], tileInk: "#2E2C29", card: "#FFFFFF", solid: "#4A6A9C", onSolid: "#FFFFFF", hi: "#F1E3B6", shadow: "#4A6A9C", live: "#7FA37A" },
  cartagrafite: { name: "Carta grafite", bg: "#F5F2EA", strip: "#ECE7DB", ink: "#34322E", muted: "#66625A", line: "#DDD6C7", accent: "#B5654A", onAccent: "#FFFFFF", tiles: ["#EBE2CA", "#E4E1D8", "#EDD9D2", "#DFE6D8"], tileInk: "#34322E", card: "#FFFFFF", solid: "#4A4743", onSolid: "#F5F2EA", hi: "#F1E3B6", shadow: "#4A4743", live: "#7FA37A" },
  cartasalvia: { name: "Carta salvia", bg: "#F5F2EA", strip: "#ECE7DB", ink: "#2F3330", muted: "#5F655F", line: "#DDD6C7", accent: "#4F7A63", onAccent: "#FFFFFF", tiles: ["#EBE2CA", "#DCE6DE", "#EDDCD6", "#E1E5EC"], tileInk: "#2F3330", card: "#FFFFFF", solid: "#4F7A63", onSolid: "#FFFFFF", hi: "#F1E3B6", shadow: "#4F7A63", live: "#C98A4B" },
  carta: { name: "Carta", bg: "#F5F3EE", strip: "#EEEBE3", ink: "#1C1F3A", muted: "#4F5268", line: "#DCD8CE", accent: "#3F5E96", onAccent: "#FFFFFF", tiles: ["#E9DFC4", "#D9E1EC", "#E9CFD5", "#DCE3D6"], tileInk: "#1C1F3A", card: "#FFFFFF", solid: "#1C1F3A", onSolid: "#FFFFFF", hi: "#E9DFC4", shadow: "#1C1F3A", live: "#7FA37A" },
  notte: { name: "Notte", bg: "#14172E", strip: "#1B1F3B", ink: "#EDE9DF", muted: "#A9ACC2", line: "#2B3054", accent: "#E3C878", onAccent: "#14172E", tiles: ["#232848", "#2A2440", "#1F2E3A", "#2B2A3E"], tileInk: "#EDE9DF", card: "#1F2342", solid: "#E3C878", onSolid: "#14172E", hi: "#3A3560", shadow: "#E3C878", live: "#8FC28A" },
  salvia: { name: "Salvia", bg: "#DCE2D3", strip: "#D1D9C6", ink: "#1F2A24", muted: "#4A5A50", line: "#BCC6B1", accent: "#A85B3C", onAccent: "#FFFFFF", tiles: ["#EEF1E8", "#C9D3BF", "#E7DCCB", "#D6DDE0"], tileInk: "#1F2A24", card: "#F4F5EF", solid: "#1F2A24", onSolid: "#F4F5EF", hi: "#EFD7C6", shadow: "#1F2A24", live: "#A85B3C" },
  argilla: { name: "Argilla", bg: "#E8D8C6", strip: "#DFCBB5", ink: "#2B1F1B", muted: "#6A574D", line: "#CDB7A1", accent: "#2F4A6B", onAccent: "#FFFFFF", tiles: ["#F3E9DD", "#D9BFA6", "#CFD5D6", "#E5CFC2"], tileInk: "#2B1F1B", card: "#F7F0E7", solid: "#2B1F1B", onSolid: "#F3E9DD", hi: "#F3E9DD", shadow: "#2B1F1B", live: "#B5653F" },
  cielo: { name: "Cielo", bg: "#D7DFEA", strip: "#CCD6E4", ink: "#141C33", muted: "#4C5770", line: "#B8C5D8", accent: "#C2577A", onAccent: "#FFFFFF", tiles: ["#EEF2F7", "#C4D1E3", "#E9D6DD", "#E6E1D2"], tileInk: "#141C33", card: "#F3F6FA", solid: "#141C33", onSolid: "#F3F6FA", hi: "#F1DCE3", shadow: "#141C33", live: "#C2577A" },
  ocra: { name: "Ocra", bg: "#E6D49B", strip: "#DECA8A", ink: "#1C1F3A", muted: "#55513A", line: "#CDBB7F", accent: "#1C1F3A", onAccent: "#E6D49B", tiles: ["#F2E8C6", "#D9C27A", "#E4E2D4", "#E8D3B6"], tileInk: "#1C1F3A", card: "#F6EFD6", solid: "#1C1F3A", onSolid: "#F2E8C6", hi: "#F6EFD6", shadow: "#1C1F3A", live: "#3F5E96" },
  menta: { name: "Menta", bg: "#D6E6DE", strip: "#C9DDD3", ink: "#12302A", muted: "#456A60", line: "#B3CDC0", accent: "#1E5C4E", onAccent: "#FFFFFF", tiles: ["#EDF5F0", "#BFD9CC", "#E4EBDF", "#D3E2E6"], tileInk: "#12302A", card: "#F2F8F4", solid: "#12302A", onSolid: "#EDF5F0", hi: "#F3E3B8", shadow: "#12302A", live: "#D08A3B" },
  bosco: { name: "Bosco", bg: "#18302A", strip: "#1E3932", ink: "#ECE7DA", muted: "#A9BDB4", line: "#2D4A42", accent: "#E0B96A", onAccent: "#18302A", tiles: ["#22403A", "#284A3F", "#1F3A3C", "#2E4636"], tileInk: "#ECE7DA", card: "#1F3B34", solid: "#E0B96A", onSolid: "#18302A", hi: "#3B5A4C", shadow: "#E0B96A", live: "#9CCB8F" },
  petrolio: { name: "Petrolio", bg: "#16363F", strip: "#1B404A", ink: "#E9EEEC", muted: "#A3BCC0", line: "#2A5560", accent: "#F0A98C", onAccent: "#16363F", tiles: ["#1F4650", "#244E55", "#1D3F4B", "#2A4C4E"], tileInk: "#E9EEEC", card: "#1C424C", solid: "#F0A98C", onSolid: "#16363F", hi: "#2F6470", shadow: "#F0A98C", live: "#8FD1C4" },
  lago: { name: "Lago", bg: "#CFE0E6", strip: "#C1D6DE", ink: "#10283A", muted: "#43627A", line: "#AFC8D2", accent: "#1F5F8B", onAccent: "#FFFFFF", tiles: ["#EAF3F6", "#B8D2DE", "#D6E7E0", "#E2E5EE"], tileInk: "#10283A", card: "#F1F7F9", solid: "#10283A", onSolid: "#EAF3F6", hi: "#FFFFFF", shadow: "#10283A", live: "#2E8B6E" },
  oceano: { name: "Oceano", bg: "#13254A", strip: "#182C56", ink: "#EAEEF6", muted: "#A6B3CF", line: "#273E6E", accent: "#8EC5E8", onAccent: "#13254A", tiles: ["#1B3160", "#20386A", "#1A3557", "#24365E"], tileInk: "#EAEEF6", card: "#1A2F5C", solid: "#8EC5E8", onSolid: "#13254A", hi: "#2A4A85", shadow: "#8EC5E8", live: "#7FD3B0" },
};

function ThemePicker({ current, setTheme }) {
  return (
    <div data-testid="theme-picker" className="fixed bottom-16 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-auto sm:bottom-auto sm:top-20 sm:right-3 z-50 max-w-[94vw] overflow-x-auto bg-white border border-[#1C1F3A] p-2 flex items-center sm:items-stretch sm:flex-col gap-1.5 shadow-[4px_4px_0_#1C1F3A]">
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#1C1F3A] px-1">Sfondo</span>
      {Object.entries(THEMES).map(([k, t]) => (
        <button key={k} data-testid={`theme-${k}`} onClick={() => setTheme(k)} title={t.name}
          className={`flex shrink-0 items-center gap-2 px-1 py-0.5 text-xs font-semibold text-[#1C1F3A] ${current === k ? "bg-[#F1F2F6]" : "hover:bg-[#F7F7F7]"}`}>
          <span className="w-5 h-5 border border-black/20 flex" style={{ background: t.bg }}><span className="w-2 h-full ml-auto" style={{ background: t.accent }} /></span>
          <span className="hidden sm:inline">{t.name}</span>
        </button>
      ))}
    </div>
  );
}

function QuietaThemed() {
  const d = useHomeData();
  const [theme, setTheme] = useState(() => new URLSearchParams(window.location.search).get("tema") || "cartablu");
  const T = THEMES[theme] || THEMES.cartablu;
  const pick = (k) => { setTheme(k); window.history.replaceState(null, "", `?tema=${k}`); };
  return (
    <div data-testid="demo-quieta" className="min-h-screen transition-colors duration-500" style={{ background: T.bg, color: T.ink, fontFamily: "'Schibsted Grotesk', sans-serif" }}>
      <ThemePicker current={theme} setTheme={pick} />
      <header className="max-w-5xl mx-auto px-5 h-16 flex items-center border-b" style={{ borderColor: T.line }}>
        <img src="/fydate-icon.png" alt="FYDATE" className="w-8 h-8" />
        <span className="ml-2 text-lg font-black tracking-tight">FYDATE</span>
        <button data-testid="quieta-login" onClick={() => d.navigate("/login")} className="ml-auto mr-14 sm:mr-24 h-10 px-5 text-sm font-bold hover:opacity-90 transition-opacity" style={{ background: T.solid, color: T.onSolid }}>Accedi</button>
      </header>

      <section className="max-w-5xl mx-auto px-5 pt-10 pb-10 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
        <div className="demo-rise">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em]" style={{ color: T.accent }}>
            <span className="w-2 h-2" style={{ background: T.live }} /> Attivo nella tua zona
          </span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black leading-[1.02] tracking-[-0.025em]">
            Hai bisogno? <span className="px-1" style={{ background: T.hi }}>Chiedilo.</span><br />
            Puoi aiutare? <span style={{ color: T.accent }}>Fallo.</span>
          </h1>
          <p className="mt-5 text-base max-w-md" style={{ color: T.muted }}>La bacheca dei bisogni del quartiere. Chi cerca trova chi offre, con calma e senza intermediari. Gratis.</p>
          <div className="mt-7 flex flex-wrap gap-2">
            <button data-testid="quieta-video" className="h-11 px-5 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform" style={{ background: T.accent, color: T.onAccent }}><Play size={14} style={{ fill: T.onAccent }} /> Guarda il video</button>
            <button data-testid="quieta-how" className="h-11 px-5 text-sm font-bold border-2 hover:-translate-y-0.5 transition-transform" style={{ borderColor: T.ink }}>Come funziona</button>
          </div>
        </div>
        <div className="relative h-60 hidden sm:block">
          <div className="absolute left-4 top-2 w-52 border p-4 demo-float" style={{ background: T.card, borderColor: T.line, boxShadow: `6px 6px 0 ${T.line}` }}>
            <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: T.muted }}>Richiesta · 2 km</p>
            <p className="mt-1.5 text-sm font-bold">Cerco idraulico per perdita in cucina</p>
            <span className="mt-3 inline-flex h-6 px-2 text-[11px] font-bold items-center" style={{ background: T.hi }}>3 disponibili</span>
          </div>
          <div className="absolute right-2 bottom-2 w-52 p-4 demo-float" style={{ background: T.solid, color: T.onSolid, boxShadow: `6px 6px 0 ${T.line}`, animationDelay: "1.5s" }}>
            <p className="text-[11px] font-bold uppercase tracking-wide opacity-60">Disponibilità</p>
            <p className="mt-1.5 text-sm font-bold">Posso passare domani mattina.</p>
            <span className="mt-3 inline-flex h-6 px-2 text-[11px] font-bold items-center bg-black/10">Risposta in 5 min</span>
          </div>
        </div>
      </section>

      <div data-testid="quieta-ticker" className="overflow-hidden whitespace-nowrap border-y text-[13px] font-semibold py-2.5" style={{ borderColor: T.line, background: T.strip }}>
        <div className="inline-block demo-marquee" style={{ animationDuration: "45s" }}>
          {[0, 1].map((k) => d.items.map((i) => (
            <span key={`${k}-${i.key}`} className="inline-flex items-center gap-2 mx-5">
              <span className="w-1.5 h-1.5" style={{ background: T.accent }} />
              {i.n !== undefined ? `${i.n} richieste · ${i.label}` : `Nuove richieste · ${i.label}`}
            </span>
          )))}
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-5 py-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div data-testid="quieta-toggle" className="inline-flex border-2" style={{ borderColor: T.ink }}>
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`quieta-mode-${k}`} onClick={() => d.setMode(k)}
                className="flex-1 h-11 px-5 text-sm font-bold whitespace-nowrap transition-colors" style={d.mode === k ? { background: T.ink, color: T.bg } : {}}>{l}</button>
            ))}
          </div>
          <form onSubmit={d.search} className="sm:flex-1 shrink-0 flex items-center h-12 border-2" style={{ borderColor: T.ink, background: T.card }}>
            <Search size={18} className="ml-4" />
            <input data-testid="quieta-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività… es. gommista" className="flex-1 h-full px-3 bg-transparent text-[15px] font-medium focus:outline-none" style={{ color: T.ink }} />
            <button type="submit" data-testid="quieta-search-submit" className="h-full w-12 flex items-center justify-center" style={{ background: T.ink, color: T.bg }}><ArrowRight size={18} /></button>
          </form>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {d.items.map((it, i) => (
            <button key={it.key} data-testid={`quieta-tile-${it.key}`} onClick={() => d.navigate(it.go)}
              className="group text-left p-4 sm:p-5 min-h-[130px] sm:min-h-[150px] flex flex-col hover:-translate-y-1 active:translate-y-0 transition-[transform,box-shadow] duration-200 demo-rise quieta-tile"
              style={{ background: T.tiles[i % 4], color: T.tileInk, animationDelay: `${i * 45}ms`, "--qs": T.shadow }}>
              <span className="flex items-start justify-between">
                <span className="w-11 h-11 flex items-center justify-center" style={{ background: T.card }}><CategoryIcon name={it.icon} size={22} /></span>
                {it.n !== undefined && it.n > 0 && <span className="h-7 px-2.5 text-xs font-extrabold flex items-center" style={{ background: T.card }}>{it.n} {it.n === 1 ? "nuova" : "nuove"}</span>}
              </span>
              <span className="mt-auto pt-4 flex items-end justify-between gap-2">
                <span className="text-base sm:text-lg font-extrabold leading-tight">{it.label}</span>
                <ArrowRight size={18} className="shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-[opacity,transform]" />
              </span>
            </button>
          ))}
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-5 pb-12">
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-4" style={{ background: T.solid, color: T.onSolid }}>
          <div>
            <h2 className="text-2xl font-black">Hai un'attività?</h2>
            <p className="text-sm opacity-70">Ti cercano già. Fatti trovare.</p>
          </div>
          <button data-testid="quieta-business" onClick={() => d.navigate("/per-attivita")} className="sm:ml-auto h-11 px-5 text-sm font-bold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform" style={{ background: T.onSolid, color: T.solid }}>Scopri come <ArrowRight size={16} /></button>
        </div>
      </footer>
    </div>
  );
}

/* ---------- 8. GIOVANE (ispirata a Billoo, colori Quieta Carta) ---------- */
const G = { paper: "#EEF1F7", ink: "#0F1230", blue: "#1F4FE0", muted: "#3C4060", line: "#DADFEA", sage: "#15924A", gold: "#FFC400" };
const GBLOB = ["#FFD23F", "#8FB2FF", "#6FE0A0", "#FFD23F", "#8FB2FF", "#6FE0A0"];
const GICON = ["#0F1230", "#0F1230", "#0F1230", "#0F1230", "#0F1230", "#0F1230"];
const SPARK = ["#1F4FE0", "#FFB800", "#15924A"];

function Sparkles({ i }) {
  const a = SPARK[i % 3]; const b = SPARK[(i + 1) % 3];
  return (
    <>
      <span className="absolute top-4 left-6 text-lg font-bold leading-none group-hover:rotate-90 transition-transform duration-150" style={{ color: a }}>+</span>
      <span className="absolute top-5 right-6 text-sm font-bold leading-none group-hover:-rotate-90 transition-transform duration-150" style={{ color: b }}>×</span>
      <span className="absolute bottom-[46%] right-[28%] text-xs leading-none" style={{ color: a }}>+</span>
    </>
  );
}

function GTile({ it, i, d, wide }) {
  return (
    <button data-testid={`giovane-tile-${it.key}`} onClick={() => d.navigate(it.go)}
      className={`group relative bg-white rounded-[22px] shadow-[0_4px_18px_-6px_rgba(21,24,51,0.18)] hover:shadow-[0_10px_24px_-8px_rgba(42,86,198,0.45)] hover:-translate-y-1 active:scale-[0.96] transition-[transform,box-shadow] duration-150 demo-pop ${wide ? "col-span-2 sm:col-span-3 flex items-center gap-5 px-6 py-5 text-left" : "flex flex-col items-center justify-end text-center px-3 pt-6 pb-5 min-h-[170px] sm:min-h-[190px]"}`}
      style={{ animationDelay: `${i * 30}ms`, animationDuration: "0.3s" }}>
      <Sparkles i={i} />
      <span className={`relative flex items-center justify-center ${wide ? "w-20 h-16" : "w-24 h-20 mb-3"}`}>
        <span className="absolute w-14 h-14 rounded-full right-1 top-0 group-hover:scale-125 transition-transform duration-150" style={{ background: GBLOB[i % 6] }} />
        <span className="relative" style={{ color: GICON[i % 6] }}><CategoryIcon name={it.icon} size={46} strokeWidth={2.1} /></span>
      </span>
      <span className={`font-extrabold leading-tight ${wide ? "text-lg sm:text-xl" : "text-base sm:text-lg"}`}>{it.label}</span>
      {it.n !== undefined && <span className="mt-1 text-xs font-semibold" style={{ color: it.n ? G.blue : G.muted }}>{it.n ? `${it.n} richieste vicino a te` : "Nessuna richiesta"}</span>}
    </button>
  );
}

function GTabBar({ d }) {
  const tabs = [["Bacheca", Home], ["Richieste", ClipboardList], null, ["Messaggi", MessageCircle], ["Profilo", User]];
  return (
    <nav data-testid="giovane-tabbar" className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none">
      <div className="pointer-events-auto relative w-full max-w-md bg-white rounded-t-[26px] shadow-[0_-6px_24px_-10px_rgba(28,31,58,0.25)] h-[72px] grid grid-cols-5 items-center">
        {tabs.map((t, i) => t ? (
          <button key={t[0]} data-testid={`giovane-tab-${t[0].toLowerCase()}`} onClick={() => i === 0 ? d.navigate("/") : d.navigate("/login")}
            className={`flex flex-col items-center gap-0.5 text-[11px] font-bold ${i === 0 ? "text-[#1F4FE0]" : "text-[#0F1230] hover:text-[#1F4FE0]"} transition-colors`}>
            {React.createElement(t[1], { size: 24, strokeWidth: 1.7 })}{t[0]}
          </button>
        ) : (
          <span key="c" className="flex justify-center">
            <button data-testid="giovane-tab-new" onClick={() => d.navigate("/login")} className="-mt-10 w-[66px] h-[66px] rounded-full flex items-center justify-center border-[5px] border-[#EEF1F7] shadow-[0_8px_20px_-6px_rgba(42,86,198,0.7)] hover:scale-110 active:scale-90 transition-transform duration-150" style={{ background: G.blue }}>
              <Plus size={30} className="text-white" strokeWidth={2.4} />
            </button>
          </span>
        ))}
      </div>
    </nav>
  );
}

function Giovane() {
  const d = useHomeData();
  const items = d.items;
  return (
    <div data-testid="demo-giovane" className="min-h-screen pb-28" style={{ background: G.paper, color: G.ink, fontFamily: "'Montserrat', sans-serif" }}>
      <header className="rounded-b-[30px] px-5 pt-5 pb-6" style={{ background: G.blue }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-4 text-white">
            <img src="/fydate-icon.png" alt="FYDATE" className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-extrabold">FYDATE</span>
            <button data-testid="giovane-login" onClick={() => d.navigate("/login")} className="ml-auto text-sm font-bold h-9 px-4 rounded-full transition-transform hover:scale-105" style={{ background: G.gold, color: G.ink }}>Accedi</button>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="giovane-zone" className="flex-1 min-w-0 h-14 bg-white rounded-full flex items-center gap-3 px-4 text-left hover:shadow-md transition-shadow">
              <MapPin size={22} strokeWidth={1.8} className="shrink-0" />
              <span className="min-w-0 flex-1"><span className="block text-sm font-bold">La tua zona</span><span className="block text-xs truncate" style={{ color: G.muted }}>Tutta Italia · tocca per scegliere</span></span>
              <ChevronRight size={20} />
            </button>
            <button data-testid="giovane-bell" className="relative text-white"><Bell size={26} strokeWidth={1.8} /><span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center" style={{ background: G.gold, color: G.ink }}>3</span></button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5">
        <div data-testid="giovane-info" className="mt-5 h-12 rounded-2xl border-2 flex items-center gap-2 px-4 font-bold text-sm sm:text-base" style={{ borderColor: G.sage, color: G.sage, background: "#DDF5E6" }}>
          <ShieldCheck size={18} /> Gratis e senza intermediari
        </div>

        <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold leading-tight">{d.mode === "need" ? "Di cosa hai bisogno?" : "Chi puoi aiutare oggi?"}</h1>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <div data-testid="giovane-toggle" className="inline-flex p-1 rounded-full bg-white shadow-[0_2px_10px_-4px_rgba(28,31,58,0.15)]">
            {[["need", "Ho bisogno"], ["help", "Posso aiutare"]].map(([k, l]) => (
              <button key={k} data-testid={`giovane-mode-${k}`} onClick={() => d.setMode(k)}
                className={`flex-1 h-10 px-5 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${d.mode === k ? "text-[#0F1230]" : "hover:text-[#1F4FE0]"}`} style={d.mode === k ? { background: G.gold } : {}}>{l}</button>
            ))}
          </div>
          <form onSubmit={d.search} className="sm:flex-1 h-12 rounded-full bg-white flex items-center shadow-[0_2px_10px_-4px_rgba(28,31,58,0.15)] focus-within:ring-2 focus-within:ring-[#2A56C6]/50">
            <Search size={18} className="ml-4" />
            <input data-testid="giovane-search" value={d.q} onChange={(e) => d.setQ(e.target.value)} placeholder="Cerca un'attività…" className="flex-1 h-full px-3 bg-transparent text-sm font-medium focus:outline-none" />
          </form>
        </div>

        <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {items.map((it, i) => <GTile key={it.key} it={it} i={i} d={d} wide={d.mode === "need" && i === items.length - 1 && items.length % 2 === 1} />)}
        </section>

        <section className="mt-8">
          <h2 className="text-xl font-extrabold mb-3">Per le attività</h2>
          <button data-testid="giovane-business" onClick={() => d.navigate("/per-attivita")} className="group relative w-full overflow-hidden rounded-[22px] p-6 text-left text-white" style={{ background: G.ink }}>
            <span className="absolute -right-8 -top-8 w-36 h-36 rounded-full opacity-60" style={{ background: G.blue }} />
            <span className="absolute right-10 bottom-6 text-2xl" style={{ color: G.gold }}>+</span>
            <span className="relative block text-sm opacity-70">Hai un'attività?</span>
            <span className="relative block mt-1 text-xl font-extrabold">Ti cercano già. <span className="px-1.5 rounded [box-decoration-break:clone] [-webkit-box-decoration-break:clone]" style={{ background: G.gold, color: G.ink }}>Fatti trovare</span></span>
            <span className="relative mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-sm font-bold group-hover:gap-3 transition-[gap] duration-150" style={{ color: G.ink }}>Scopri come <ArrowRight size={16} /></span>
          </button>
        </section>
      </main>
      <GTabBar d={d} />
    </div>
  );
}

const VARIANTS = {
  giovane: { C: Giovane, label: "Giovane" },
  quieta: { C: QuietaThemed, label: "Quieta" },
  viva: { C: Viva, label: "Viva" },
  morbido: { C: SoftSwiss, label: "Svizzero morbido" },
  curato: { C: Refined, label: "Attuale curato" },
  bauhaus: { C: Bauhaus, label: "Bauhaus classico" },
  svizzero: { C: Swiss, label: "Minimal svizzero" },
  editoriale: { C: Editorial, label: "Editoriale a griglia" },
};

function Switcher({ current }) {
  const top = current === "giovane";
  return (
    <div data-testid="demo-switcher" className={`fixed ${top ? "bottom-[84px] left-1/2 -translate-x-1/2 opacity-70 hover:opacity-100" : "bottom-4 left-1/2 -translate-x-1/2"} z-50 flex max-w-[95vw] overflow-x-auto rounded-full bg-[#0B0D2B] p-1 shadow-xl text-xs font-semibold transition-opacity`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {Object.entries(VARIANTS).map(([k, v]) => (
        <Link key={k} to={`/demo-design/${k}`} data-testid={`demo-switch-${k}`} className={`px-3 h-9 rounded-full flex items-center whitespace-nowrap transition-colors ${current === k ? "bg-[#FFD23F] text-[#0B0D2B]" : "text-white hover:text-[#FFD23F]"}`}>{v.label}</Link>
      ))}
    </div>
  );
}

export default function DemoDesign() {
  useFonts();
  const { variant = "giovane" } = useParams();
  const V = (VARIANTS[variant] || VARIANTS.giovane).C;
  useEffect(() => { window.scrollTo(0, 0); }, [variant]);
  return (
    <div className="pb-16 demo-root">
      <V />
      <Switcher current={VARIANTS[variant] ? variant : "giovane"} />
    </div>
  );
}
